import { useEffect, useState } from 'react';
import { talentApi } from '../../../api/talent.js';
import { hrApi } from '../../../api/hr.js';

const READINESS = ['ready_now', '1_2_years', '3_plus'];
const READINESS_LABEL = { ready_now: 'Ready now', '1_2_years': '1–2 years', '3_plus': '3+ years' };

/**
 * Succession planning board — critical roles, candidates by readiness, and a
 * bench-strength summary per plan.
 */
export default function Succession() {
  const [plans, setPlans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  function refresh() {
    setLoading(true);
    Promise.all([talentApi.listPlans(), hrApi.listEmployees().catch(() => ({ items: [] }))])
      .then(([p, e]) => {
        setPlans(p?.items || []);
        setEmployees(e?.items || e || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function openPlan(planId) {
    try {
      setSelected(await talentApi.getPlan(planId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load plan');
    }
  }

  async function createPlan(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await talentApi.createPlan({
        criticalRole: fd.get('criticalRole'),
        incumbentId: fd.get('incumbentId') || null,
        riskOfLoss: fd.get('riskOfLoss') || null,
        impactOfLoss: fd.get('impactOfLoss') || null,
      });
      e.target.reset();
      setShowCreate(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create plan');
    }
  }

  async function addCandidate(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await talentApi.addCandidate(selected.plan.id, {
        employeeId: fd.get('employeeId'),
        readiness: fd.get('readiness') || 'ready_now',
        notes: fd.get('notes') || null,
      });
      e.target.reset();
      openPlan(selected.plan.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add candidate');
    }
  }

  return (
    <section data-testid="talent-succession-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Succession</h1>
          <p className="muted">{plans.length} critical roles</p>
        </div>
        <button className="btn primary" data-testid="talent-succession-create" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Close' : '+ Plan'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="talent-succession-error">{error}</div>}

      {showCreate && (
        <form onSubmit={createPlan} className="card form-card grid-form">
          <label>Critical role<input name="criticalRole" data-testid="talent-succession-role-input" required /></label>
          <label>Incumbent
            <select name="incumbentId" defaultValue="">
              <option value="">— none —</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
            </select>
          </label>
          <label>Risk of loss
            <select name="riskOfLoss" defaultValue="medium"><option value="low">low</option><option value="medium">medium</option><option value="high">high</option></select>
          </label>
          <label>Impact of loss
            <select name="impactOfLoss" defaultValue="high"><option value="low">low</option><option value="medium">medium</option><option value="high">high</option></select>
          </label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="talent-succession-submit">Create plan</button></div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="talent-succession-list">
          <thead><tr><th>Critical role</th><th>Incumbent</th><th>Risk</th><th>Impact</th><th></th></tr></thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} data-testid={`talent-succession-row-${p.id}`}>
                <td>{p.critical_role || p.position_title || '—'}</td>
                <td>{p.incumbent_name || '—'}</td>
                <td>{p.risk_of_loss || '—'}</td>
                <td>{p.impact_of_loss || '—'}</td>
                <td><button className="btn ghost" data-testid={`talent-succession-open-${p.id}`} onClick={() => openPlan(p.id)}>View bench</button></td>
              </tr>
            ))}
            {!loading && plans.length === 0 && <tr><td colSpan={5} className="muted">No plans yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="card" data-testid="talent-succession-detail">
          <h3>{selected.plan.critical_role || selected.plan.position_title} — bench strength</h3>
          <p className="muted" data-testid="talent-bench-strength">
            {selected.benchStrength.total} candidate(s) · {selected.benchStrength.readyNow} ready now · score {selected.benchStrength.score}
          </p>
          <div className="ninebox-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {READINESS.map((r) => (
              <div key={r} className="kcol" data-testid={`talent-readiness-${r}`} style={{ padding: 8, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <header><span className="kcol-name">{READINESS_LABEL[r]}</span></header>
                {selected.candidates.filter((c) => c.readiness === r).map((c) => (
                  <div key={c.id} className="issue-card" data-testid={`talent-candidate-${c.id}`}>{c.employee_name || c.employee_id}</div>
                ))}
              </div>
            ))}
          </div>
          <form onSubmit={addCandidate} className="form-card grid-form" style={{ marginTop: 12 }}>
            <label>Employee
              <select name="employeeId" data-testid="talent-candidate-employee-input" required defaultValue="">
                <option value="" disabled>— select —</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
              </select>
            </label>
            <label>Readiness
              <select name="readiness" defaultValue="ready_now">
                {READINESS.map((r) => <option key={r} value={r}>{READINESS_LABEL[r]}</option>)}
              </select>
            </label>
            <label className="full">Notes<input name="notes" /></label>
            <div className="full actions"><button className="btn primary" type="submit" data-testid="succession-candidate-add">Add candidate</button></div>
          </form>
        </div>
      )}
    </section>
  );
}
